import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import type { QuotationStatus } from "./api";

/** 1 ページの件数。 */
export const QUOTATIONS_PAGE_SIZE = 50;

export interface QuotationListParams {
  page: number;
  keyword: string;
  status: QuotationStatus | null;
  sortKey: string;
  ascending: boolean;
}

const DEFAULTS: QuotationListParams = {
  page: 1,
  keyword: "",
  status: null,
  // 見積番号は採番順＝作成順なので、created_at で並べるのと結果は同じ。列として
  // 存在するキーにしておくことで、どの列で並んでいるかが一覧の矢印で分かる。
  sortKey: "quotation_number",
  ascending: false, // 新しい見積を先頭に出す
};

/**
 * 一覧の検索条件・並び替え・ページをURLのクエリに持つ。
 *
 * localStorage ではなく URL に置くのは、リロードで復元されるだけでなく
 * **URL を共有でき、ブラウザの戻る/進むが効く**ため。表示列・列幅・列順は
 * 個人の見た目設定なので localStorage 側（[[useTableSettings]]）に分ける。
 */
export function useQuotationListParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<QuotationListParams>(() => {
    const page = Number(searchParams.get("page") ?? DEFAULTS.page);
    const status = searchParams.get("status");
    return {
      page: Number.isFinite(page) && page >= 1 ? page : DEFAULTS.page,
      keyword: searchParams.get("keyword") ?? DEFAULTS.keyword,
      status: (status as QuotationStatus | null) ?? DEFAULTS.status,
      sortKey: searchParams.get("sort_key") ?? DEFAULTS.sortKey,
      ascending: searchParams.get("ascending") === "true",
    };
  }, [searchParams]);

  const update = useCallback(
    (patch: Partial<QuotationListParams>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const merged = { ...params, ...patch };
          // 条件を変えたらページを 1 に戻す（page 自体の変更を除く）。
          if (patch.page === undefined) merged.page = 1;

          const set = (key: string, value: string, fallback: string) =>
            value === fallback ? next.delete(key) : next.set(key, value);

          set("page", String(merged.page), String(DEFAULTS.page));
          set("keyword", merged.keyword, DEFAULTS.keyword);
          set("status", merged.status ?? "", "");
          set("sort_key", merged.sortKey, DEFAULTS.sortKey);
          set("ascending", String(merged.ascending), String(DEFAULTS.ascending));
          return next;
        },
        { replace: true },
      );
    },
    [params, setSearchParams],
  );

  /** 絞り込み・並び替え・ページを既定値に戻す。 */
  const reset = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  /** 有効な絞り込み条件の数（「条件あり」の表示に使う）。 */
  const activeFilterCount = (params.keyword ? 1 : 0) + (params.status ? 1 : 0);

  return { params, update, reset, activeFilterCount };
}
